import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import {
  UploadCloud,
  FileText,
  Sparkles,
  Trash2,
  RefreshCcw,
  FileJson,
  BadgeCheck,
  LayoutDashboard,
  Boxes,
  X,
  Barcode,
  Activity,
  MoreVertical,
  RotateCcw,
  ArrowUp,
  ArrowDown,
  ArrowUpDown,
  Mail,
  Settings,
  Search,
  Zap,
} from "lucide-react";

import { api, formatCurrency, formatDate, truncHash } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import ReviewDialog from "@/components/ReviewDialog";
import MintedDialog from "@/components/MintedDialog";
import FulfillDialog from "@/components/FulfillDialog";
import MintDetailsDialog from "@/components/MintDetailsDialog";
import ActivityLogDialog from "@/components/ActivityLogDialog";
import AdminPanel from "@/components/AdminPanel";
import EmailNotificationsPanel from "@/components/EmailNotificationsPanel";
import RecordDetailDrawer from "@/components/RecordDetailDrawer";
import SystemStatusCard from "@/components/SystemStatusCard";
import QuickIssueDialog from "@/components/QuickIssueDialog";

const StatusBadge = ({ status }) => {
  const map = {
    minted: {
      testid: "status-minted",
      cls: "bg-blue-50 text-blue-700 border border-blue-200",
      dot: "bg-[#0055FF]",
      label: "Certified",
    },
    pending_data: {
      testid: "status-pending_data",
      cls: "bg-amber-50 text-amber-800 border border-amber-200",
      dot: "bg-amber-500",
      label: "Pending Data",
    },
    ready_to_mint: {
      testid: "status-ready_to_mint",
      cls: "bg-emerald-50 text-emerald-800 border border-emerald-200",
      dot: "bg-emerald-500",
      label: "Ready to Issue",
    },
    minting: {
      testid: "status-minting",
      cls: "bg-indigo-50 text-indigo-800 border border-indigo-200",
      dot: "bg-indigo-500 animate-pulse",
      label: "Issuing…",
    },
    mint_failed: {
      testid: "status-mint_failed",
      cls: "bg-red-50 text-red-700 border border-red-200",
      dot: "bg-red-500",
      label: "Issuance Failed",
    },
    pending_fulfillment: {
      testid: "status-pending_fulfillment",
      cls: "bg-amber-50 text-amber-800 border border-amber-200",
      dot: "bg-amber-500",
      label: "Pending Fulfillment",
    },
    revoked: {
      testid: "status-revoked",
      cls: "bg-red-50 text-red-700 border border-red-200",
      dot: "bg-red-500",
      label: "Invalidated",
    },
  };
  const m = map[status] || {
    testid: `status-${status}`,
    cls: "bg-gray-100 text-gray-700",
    dot: "bg-gray-400",
    label: status || "Unknown",
  };
  return (
    <span
      data-testid={m.testid}
      className={`inline-flex items-center gap-1.5 px-2.5 py-0.5 rounded-full text-xs font-semibold ${m.cls}`}
    >
      <span className={`h-1.5 w-1.5 rounded-full ${m.dot}`} />
      {m.label}
    </span>
  );
};

function RowAction({ r, mintingId, onMint, onOpenDialog }) {
  const base =
    "inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm transition";
  const primary = "bg-[#0055FF] text-white hover:bg-[#0044CC]";
  const secondary =
    "bg-white text-gray-900 border border-gray-200 hover:border-[#0055FF] hover:text-[#0055FF]";

  if (r.status === "minted") {
    return (
      <Link
        to={`/verify/${r.nft_id}`}
        onClick={(e) => e.stopPropagation()}
        data-testid={`view-cert-btn-${r.id}`}
        className={`${base} ${secondary}`}
      >
        <BadgeCheck size={13} />
        View Certificate
      </Link>
    );
  }
  if (r.status === "pending_data" || r.status === "pending_fulfillment") {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpenDialog(r);
        }}
        data-testid={`complete-btn-${r.id}`}
        className={`${base} bg-amber-500 text-white hover:bg-amber-600 shadow-none`}
      >
        <Barcode size={13} />
        Complete
      </button>
    );
  }
  if (r.status === "ready_to_mint") {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpenDialog(r);
        }}
        data-testid={`mint-btn-${r.id}`}
        className={`${base} ${primary}`}
      >
        <Sparkles size={13} />
        Issue Certificate
      </button>
    );
  }
  if (r.status === "mint_failed") {
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onOpenDialog(r);
        }}
        data-testid={`retry-btn-${r.id}`}
        className={`${base} bg-red-600 text-white hover:bg-red-700`}
      >
        <RefreshCcw size={13} />
        Retry Issuance
      </button>
    );
  }
  if (r.status === "minting") {
    return (
      <button
        disabled
        data-testid={`minting-btn-${r.id}`}
        className={`${base} bg-indigo-400 text-white opacity-80 cursor-wait`}
      >
        <Sparkles size={13} />
        Issuing…
      </button>
    );
  }
  if (r.status === "pending") {
    // Legacy manual pending — keep existing mock mint path
    return (
      <button
        onClick={(e) => {
          e.stopPropagation();
          onMint(r.id);
        }}
        disabled={mintingId === r.id}
        data-testid={`mint-btn-${r.id}`}
        className={`${base} ${primary} disabled:opacity-60 disabled:cursor-wait`}
      >
        <Sparkles size={13} />
        {mintingId === r.id ? "Issuing…" : "Issue Certificate"}
      </button>
    );
  }
  return <span className="text-xs text-gray-400 font-medium">—</span>;
}

function RowMenu({ r, onViewActivity, onRequeue, onDelete }) {
  const [open, setOpen] = useState(false);
  const [pos, setPos] = useState({ top: 0, left: 0 });
  const btnRef = useRef(null);
  const menuRef = useRef(null);

  // Compute placement from the button's current viewport rect.
  const computePlacement = () => {
    if (!btnRef.current) return;
    const rect = btnRef.current.getBoundingClientRect();
    const menuWidth = 208;
    const left = Math.max(
      8,
      Math.min(window.innerWidth - menuWidth - 8, rect.right - menuWidth),
    );
    setPos({ top: rect.bottom + 4, left });
  };

  // Close on outside click using mousedown (capture phase) so we beat any
  // React click handlers and don't depend on event-bubbling timing.
  // Also close on Escape and on real wheel/scroll movements (not just any
  // synthetic scroll fired during layout) — this avoids the previous bug
  // where opening the menu could synthesise a scroll and immediately close.
  useEffect(() => {
    if (!open) return;
    const onMouseDown = (e) => {
      if (menuRef.current?.contains(e.target)) return;
      if (btnRef.current?.contains(e.target)) return;
      setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    const onWheel = () => setOpen(false);
    document.addEventListener("mousedown", onMouseDown, true);
    document.addEventListener("keydown", onKey);
    window.addEventListener("wheel", onWheel, { passive: true });
    window.addEventListener("resize", computePlacement);
    return () => {
      document.removeEventListener("mousedown", onMouseDown, true);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("resize", computePlacement);
    };
  }, [open]);

  const handleToggle = (e) => {
    // Defensive — block both bubble (drawer trigger on the row) AND any
    // capture-phase listeners that may have been registered elsewhere.
    e.preventDefault();
    e.stopPropagation();
    if (!open) computePlacement();
    setOpen((v) => !v);
  };

  const canRequeue = !["minted", "minting"].includes(r.status);
  const requeueBlockedMsg =
    r.status === "minted"
      ? "This record is already minted"
      : r.status === "minting"
        ? "Cannot requeue while a mint is in progress"
        : null;

  const handleViewActivity = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    onViewActivity(r);
  };

  const handleRequeueClick = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    if (!canRequeue) {
      toast.error(requeueBlockedMsg || "Cannot requeue");
      return;
    }
    onRequeue(r.id);
  };

  const handleDelete = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setOpen(false);
    onDelete(r.id);
  };

  // Render the menu via a portal so transforms / overflow on ancestors can't
  // clip or hide it. zIndex 9999 keeps it above all in-flow content.
  const menu =
    open && typeof document !== "undefined"
      ? createPortal(
          <div
            ref={menuRef}
            data-testid={`row-menu-${r.id}`}
            style={{
              position: "fixed",
              top: pos.top,
              left: pos.left,
              width: 208,
              zIndex: 9999,
            }}
            className="rounded-lg border border-gray-200 bg-white shadow-lg py-1 text-sm"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <button
              type="button"
              onClick={handleViewActivity}
              data-testid={`menu-view-activity-${r.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-gray-900"
            >
              <Activity size={14} className="text-gray-500" />
              View Activity
            </button>
            <button
              type="button"
              onClick={handleRequeueClick}
              disabled={!canRequeue}
              data-testid={`menu-requeue-${r.id}`}
              title={requeueBlockedMsg || "Requeue"}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-gray-50 text-gray-900 disabled:text-gray-400 disabled:cursor-not-allowed"
            >
              <RotateCcw
                size={14}
                className={canRequeue ? "text-violet-600" : "text-gray-400"}
              />
              Requeue
            </button>
            <div className="h-px bg-gray-100 my-1" />
            <button
              type="button"
              onClick={handleDelete}
              data-testid={`menu-delete-${r.id}`}
              className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-red-50 text-red-700"
            >
              <Trash2 size={14} />
              Delete
            </button>
          </div>,
          document.body,
        )
      : null;

  return (
    <div className="relative inline-flex" onClick={(e) => e.stopPropagation()}>
      <button
        ref={btnRef}
        type="button"
        onClick={handleToggle}
        data-testid={`row-menu-btn-${r.id}`}
        title="Actions"
        className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 transition"
      >
        <MoreVertical size={14} />
      </button>
      {menu}
    </div>
  );
}

// ---------- Sortable columns (shared by Overview + Queue) ----------
// Column key → extractor. Extractors return a value we can compare with <.
// Date columns are parsed as epoch ms (timestamps) so chronological order is
// reliable regardless of input format ("2026-04-23" vs "2026-04-23T12:00:00Z"
// vs "Apr 23, 2026"). Strings compared case-insensitively.
const parseDateMs = (d) => {
  if (!d) return NaN;
  const t = new Date(d).getTime();
  return Number.isFinite(t) ? t : NaN;
};
const SORT_EXTRACTORS = {
  created_at: (r) => parseDateMs(r.created_at),
  purchase_date: (r) => parseDateMs(r.purchase_date),
  minted_at: (r) => parseDateMs(r.minted_at),
  order_id: (r) => r.source_order_id || r.receipt_id || "",
  status: (r) => r.status || "",
  customer: (r) => (r.customer_email || r.customer_name || "").toLowerCase(),
  total: (r) => (typeof r.total === "number" ? r.total : -Infinity),
  store: (r) => (r.store_name || "").toLowerCase(),
  item: (r) => ((r.items?.[0]?.name) || "").toLowerCase(),
};

function sortRows(rows, column, dir) {
  const extractor = SORT_EXTRACTORS[column] || SORT_EXTRACTORS.created_at;
  const copy = [...rows];
  copy.sort((a, b) => {
    const va = extractor(a);
    const vb = extractor(b);
    // Empty / null values ALWAYS sort to the bottom regardless of direction.
    // This is what users expect for Verified Date (uncertified records last)
    // and is the same convention applied uniformly across columns.
    const isEmpty = (v) =>
      v === "" ||
      v === null ||
      v === undefined ||
      v === -Infinity ||
      (typeof v === "number" && Number.isNaN(v));
    const aEmpty = isEmpty(va);
    const bEmpty = isEmpty(vb);
    if (aEmpty && !bEmpty) return 1;
    if (!aEmpty && bEmpty) return -1;
    if (aEmpty && bEmpty) return 0;
    if (va < vb) return dir === "asc" ? -1 : 1;
    if (va > vb) return dir === "asc" ? 1 : -1;
    return 0;
  });
  return copy;
}

function SortableHeader({ label, column, sort, onSort, align = "left", testid, tooltip }) {
  const active = sort.column === column;
  const dir = active ? sort.dir : null;
  const Icon = dir === "asc" ? ArrowUp : dir === "desc" ? ArrowDown : ArrowUpDown;
  const justify = align === "right" ? "justify-end" : "justify-start";
  return (
    <th
      scope="col"
      className={`px-5 py-3.5 ${align === "right" ? "text-right" : "text-left"}`}
    >
      <button
        type="button"
        onClick={() => onSort(column)}
        data-testid={testid || `sort-${column}`}
        title={tooltip}
        className={`inline-flex items-center gap-1.5 ${justify} text-[11px] font-semibold tracking-wider uppercase transition ${
          active ? "text-[#0055FF]" : "text-gray-500 hover:text-gray-900"
        }`}
      >
        <span>{label}</span>
        <Icon
          size={12}
          className={active ? "opacity-100" : "opacity-40"}
        />
      </button>
    </th>
  );
}

// ---------- Filter chips (Overview tab) ----------
const FILTER_CHIPS = [
  { id: "all",      label: "All",      statuses: null },
  { id: "minted",   label: "Certified",   statuses: ["minted"] },
  { id: "ready",    label: "Ready",    statuses: ["ready_to_mint"] },
  { id: "pending",  label: "Pending",  statuses: ["pending_data", "pending_fulfillment", "pending"] },
  { id: "minting",  label: "Issuing",  statuses: ["minting"] },
  { id: "failed",   label: "Failed",   statuses: ["mint_failed"] },
  { id: "revoked",  label: "Invalidated",  statuses: ["revoked"] },
];

function matchesFilter(receipt, filterId) {
  const chip = FILTER_CHIPS.find((c) => c.id === filterId);
  if (!chip || !chip.statuses) return true;
  return chip.statuses.includes(receipt.status);
}

function countByFilter(receipts) {
  const counts = {};
  for (const chip of FILTER_CHIPS) {
    counts[chip.id] = chip.statuses
      ? receipts.filter((r) => chip.statuses.includes(r.status)).length
      : receipts.length;
  }
  return counts;
}

function FilterChipBar({ active, counts, onChange }) {
  return (
    <div
      data-testid="overview-filter-chips"
      className="flex flex-wrap items-center gap-1.5 mb-4"
    >
      {FILTER_CHIPS.map((chip) => {
        const isActive = chip.id === active;
        const n = counts[chip.id] ?? 0;
        if (!isActive && chip.id !== "all" && n === 0) return null;
        return (
          <button
            key={chip.id}
            onClick={() => onChange(chip.id)}
            data-testid={`filter-chip-${chip.id}`}
            className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-xs font-semibold transition ${
              isActive
                ? "bg-gray-900 text-white"
                : "bg-white text-gray-700 border border-gray-200 hover:border-gray-900"
            }`}
          >
            {chip.label}
            <span
              className={`text-[10px] font-mono tabular-nums ${
                isActive ? "text-white/70" : "text-gray-400"
              }`}
            >
              {n}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function UploadDropzone({ onFiles, uploading }) {
  const fileInputRef = useRef(null);
  const [dragActive, setDragActive] = useState(false);
  return (
    <div
      data-testid="upload-zone"
      onClick={() => fileInputRef.current?.click()}
      onDragOver={(e) => {
        e.preventDefault();
        setDragActive(true);
      }}
      onDragLeave={() => setDragActive(false)}
      onDrop={(e) => {
        e.preventDefault();
        setDragActive(false);
        onFiles(e.dataTransfer.files);
      }}
      className={`upload-zone cursor-pointer border-2 border-dashed rounded-xl p-10 text-center bg-gray-50/50 ${
        dragActive ? "border-[#0055FF] bg-blue-50/40" : "border-gray-300"
      }`}
    >
      <input
        ref={fileInputRef}
        type="file"
        accept=".pdf,.json,application/pdf,application/json"
        className="hidden"
        data-testid="file-input"
        onChange={(e) => onFiles(e.target.files)}
      />
      <div className="mx-auto h-12 w-12 rounded-xl bg-white border border-gray-200 flex items-center justify-center shadow-sm">
        <UploadCloud className="text-[#0055FF]" size={22} />
      </div>
      <div className="mt-4 font-semibold text-gray-900">
        {uploading ? "Parsing…" : "Drop a receipt PDF or JSON"}
      </div>
      <div className="mt-1 text-sm text-gray-500">
        or <span className="text-[#0055FF] font-medium">click to browse</span> ·
        editable preview before save
      </div>
      <div className="mt-4 inline-flex items-center gap-4 text-xs text-gray-400">
        <span className="inline-flex items-center gap-1.5">
          <FileText size={12} /> .pdf
        </span>
        <span className="inline-flex items-center gap-1.5">
          <FileJson size={12} /> .json
        </span>
      </div>
    </div>
  );
}

function UploadModal({ open, onClose, onFiles, uploading }) {
  if (!open) return null;
  return (
    <div
      data-testid="upload-modal"
      className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-gray-900/40 backdrop-blur-[2px]"
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-lg bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
          <div>
            <div className="font-display font-semibold text-gray-900">
              Manual Receipt Upload
            </div>
            <div className="text-[11px] tracking-wider uppercase text-gray-500">
              PDF or JSON · editable preview before save
            </div>
          </div>
          <button
            onClick={onClose}
            data-testid="upload-modal-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100"
          >
            <X size={16} />
          </button>
        </div>
        <div className="p-6">
          <UploadDropzone onFiles={onFiles} uploading={uploading} />
        </div>
      </div>
    </div>
  );
}

// ---------- LocalStorage helpers (UI state persistence) ----------
const LS_KEY = "authera.retailer.uistate.v1";

function readPersistedUiState() {
  if (typeof window === "undefined") return {};
  try {
    const raw = window.localStorage.getItem(LS_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    return typeof parsed === "object" && parsed !== null ? parsed : {};
  } catch {
    return {};
  }
}

function writePersistedUiState(state) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(LS_KEY, JSON.stringify(state));
  } catch {
    // quota or disabled — silently ignore
  }
}

export default function Retailer() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const persisted = readPersistedUiState();
  const [receipts, setReceipts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [reviewData, setReviewData] = useState(null);
  const [mintedData, setMintedData] = useState(null);
  const [mintingId, setMintingId] = useState(null);
  const [activeTab, setActiveTab] = useState(persisted.activeTab || "overview");
  const [uploadOpen, setUploadOpen] = useState(false);
  const [quickIssueOpen, setQuickIssueOpen] = useState(false);
  const [fulfillTarget, setFulfillTarget] = useState(null);
  const [mintTarget, setMintTarget] = useState(null);
  const [activityTarget, setActivityTarget] = useState(null);
  const [detailTarget, setDetailTarget] = useState(null);
  const [queueSearch, setQueueSearch] = useState("");
  const [overviewSort, setOverviewSort] = useState(
    persisted.overviewSort || { column: "minted_at", dir: "desc" }
  );
  const [queueSort, setQueueSort] = useState(
    persisted.queueSort || { column: "purchase_date", dir: "desc" }
  );
  const [overviewFilter, setOverviewFilter] = useState(persisted.overviewFilter || "all");
  const [overviewSearch, setOverviewSearch] = useState("");
  const [overviewPage, setOverviewPage] = useState(1);
  const [overviewPageSize, setOverviewPageSize] = useState(
    persisted.overviewPageSize || 10,
  );

  // Persist UI state to localStorage whenever it changes
  useEffect(() => {
    writePersistedUiState({
      activeTab,
      overviewSort,
      queueSort,
      overviewFilter,
      overviewPageSize,
    });
  }, [activeTab, overviewSort, queueSort, overviewFilter, overviewPageSize]);

  // Reset to page 1 whenever the result set is filtered/searched/sorted/sized.
  // This is the standard pagination expectation — without it a user could be
  // on page 5 of "all" and then narrow the search to a 3-item match and see
  // an empty page.
  useEffect(() => {
    setOverviewPage(1);
  }, [overviewFilter, overviewSearch, overviewSort.column, overviewSort.dir, overviewPageSize]);

  const toggleSort = (current, setter) => (column) => {
    if (current.column !== column) {
      setter({ column, dir: "asc" });
    } else {
      setter({ column, dir: current.dir === "asc" ? "desc" : "asc" });
    }
  };

  const load = async () => {
    try {
      setLoading(true);
      const { data } = await api.get("/receipts");
      setReceipts(data || []);
    } catch {
      toast.error("Failed to load receipts");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const handleFiles = async (files) => {
    const file = files?.[0];
    if (!file) return;
    const ext = file.name.split(".").pop().toLowerCase();
    if (!["pdf", "json"].includes(ext)) {
      toast.error("Please upload a PDF or JSON file");
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/receipts/parse", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setUploadOpen(false);
      setReviewData({
        ...data.preview,
        storage_path: data.storage_path,
        original_filename: data.original_filename,
      });
      toast.success("Receipt parsed. Review before saving.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to parse receipt");
    } finally {
      setUploading(false);
    }
  };

  const handleSave = async (payload) => {
    try {
      await api.post("/receipts", payload);
      toast.success("Receipt saved");
      setReviewData(null);
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    }
  };

  const handleMint = async (id) => {
    setMintingId(id);
    try {
      const { data } = await api.post(`/receipts/${id}/mint`);
      setMintedData(data);
      toast.success("Certificate issued successfully");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Issuance failed");
    } finally {
      setMintingId(null);
    }
  };

  const handleDelete = async (id) => {
    try {
      await api.delete(`/receipts/${id}`);
      toast.success("Receipt deleted");
      await load();
    } catch {
      toast.error("Delete failed");
    }
  };

  const handleReset = async () => {
    try {
      await api.post("/admin/reset");
      toast.success("Demo data reseeded");
      await load();
    } catch {
      toast.error("Reset failed");
    }
  };

  const handleRequeue = async (id) => {
    try {
      await api.post(`/receipts/${id}/requeue`);
      toast.success("Record requeued successfully");
      await load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Requeue failed");
    }
  };

  const handleTestEmail = async () => {
    const to = window.prompt(
      "Send a test certificate email to which address?",
      "rjmortero@gmail.com",
    );
    if (!to) return;
    try {
      const { data } = await api.post("/admin/test_email", { to });
      toast.success(`Email notification sent to ${data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Test email failed");
    }
  };

  const onFulfilled = (updated) => {
    setFulfillTarget(null);
    toast.success(`Certificate issued · ${updated.nft_id || "on-chain"}`);
    load();
  };

  const totalMinted = receipts.filter((r) => r.status === "minted").length;
  const totalPending = receipts.filter((r) =>
    ["pending", "pending_data", "pending_fulfillment", "ready_to_mint", "minting", "mint_failed"].includes(
      r.status,
    ),
  ).length;
  const totalValue = receipts.reduce((s, r) => s + (r.total || 0), 0);

  // Apply filter chip → free-text search → sort, then paginate.
  // Search fields per spec: receipt_id, item name, store_name, customer_email,
  // sku, tx_hash, status (case-insensitive substring on each).
  const overviewBeforeSearch = receipts.filter((r) =>
    matchesFilter(r, overviewFilter),
  );
  const overviewSearched = overviewSearch.trim()
    ? overviewBeforeSearch.filter((r) => {
        const q = overviewSearch.trim().toLowerCase();
        const item = r.items?.[0] || {};
        return (
          (r.receipt_id || "").toLowerCase().includes(q) ||
          (r.source_order_id || "").toLowerCase().includes(q) ||
          (item.name || "").toLowerCase().includes(q) ||
          (item.sku || "").toLowerCase().includes(q) ||
          (r.store_name || "").toLowerCase().includes(q) ||
          (r.customer_email || "").toLowerCase().includes(q) ||
          (r.tx_hash || "").toLowerCase().includes(q) ||
          (r.status || "").toLowerCase().includes(q)
        );
      })
    : overviewBeforeSearch;
  const overviewSorted = sortRows(
    overviewSearched,
    overviewSort.column,
    overviewSort.dir,
  );
  const overviewTotal = overviewSorted.length;
  const overviewTotalPages = Math.max(
    1,
    Math.ceil(overviewTotal / overviewPageSize),
  );
  const overviewSafePage = Math.min(overviewPage, overviewTotalPages);
  const overviewStart = (overviewSafePage - 1) * overviewPageSize;
  const overviewEnd = Math.min(overviewStart + overviewPageSize, overviewTotal);
  const overviewReceipts = overviewSorted.slice(overviewStart, overviewEnd);
  const filterCounts = countByFilter(receipts);

  // Fulfillment queue: any record that's not yet minted/revoked.
  // Ordering is driven by `queueSort` state; default created_at DESC (newest first).
  const pendingStatuses = new Set([
    "pending_data",
    "pending_fulfillment",
    "ready_to_mint",
    "mint_failed",
    "minting",
  ]);
  const queueItemsRaw = receipts.filter((r) => pendingStatuses.has(r.status));
  const queueItems = sortRows(queueItemsRaw, queueSort.column, queueSort.dir);
  const filteredQueue = queueSearch.trim()
    ? queueItems.filter((r) => {
        const q = queueSearch.trim().toLowerCase();
        const item = r.items?.[0]?.name || "";
        return (
          (r.receipt_id || "").toLowerCase().includes(q) ||
          (r.source_order_id || "").toLowerCase().includes(q) ||
          (r.customer_email || "").toLowerCase().includes(q) ||
          item.toLowerCase().includes(q)
        );
      })
    : queueItems;

  return (
    <div
      data-testid="retailer-page"
      className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10"
    >
      <div className="flex items-start justify-between mb-2 gap-4">
        <div>
          <div className="text-xs font-semibold tracking-wider uppercase text-[#0055FF]">
            {isAdmin ? "Admin Dashboard" : "Retailer Dashboard"}
          </div>
          <h1
            data-testid="retailer-title"
            className="font-display font-bold tracking-tight text-gray-900 text-3xl sm:text-4xl mt-1"
          >
            {isAdmin
              ? "All Receipts & Mints"
              : `Dashboard — ${user?.retailer_name || "Retailer"}`}
          </h1>
          {!isAdmin && user?.retailer_id && (
            <div className="mt-1 text-[11px] font-mono text-gray-500">
              {user.retailer_id}
            </div>
          )}
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <button
            onClick={() => setQuickIssueOpen(true)}
            data-testid="open-quick-issue-btn"
            className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white border border-[#0055FF] text-sm font-semibold px-3 py-2 rounded-md hover:bg-[#0044CC] hover:border-[#0044CC] transition shadow-sm"
            title="Issue a certificate in ~10 seconds"
          >
            <Zap size={14} fill="currentColor" />
            Quick Issue
          </button>
          <button
            onClick={() => setUploadOpen(true)}
            data-testid="open-upload-btn"
            className="inline-flex items-center gap-1.5 bg-white text-gray-900 border border-gray-200 text-sm font-semibold px-3 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] transition"
          >
            <UploadCloud size={14} />
            Upload Receipt
          </button>
          <button
            onClick={handleTestEmail}
            data-testid="test-email-btn"
            title="Send a test certificate email"
            className="inline-flex items-center gap-1.5 bg-white text-gray-900 border border-gray-200 text-sm font-semibold px-3 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] transition"
          >
            <Mail size={14} />
            Test Email
          </button>
          <button
            onClick={handleReset}
            data-testid="reset-demo-btn"
            className="inline-flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-gray-900 px-3 py-2 rounded-lg hover:bg-gray-100 transition"
            title="Reseed demo data"
          >
            <RefreshCcw size={14} />
            Reset demo
          </button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid sm:grid-cols-3 gap-4 mt-8">
        {[
          {
            label: "Total Receipts",
            value: receipts.length,
            tone: "default",
            testid: "stat-total",
          },
          {
            label: "Certified",
            value: totalMinted,
            tone: "primary",
            testid: "stat-minted",
          },
          {
            label: "Gross Value",
            value: formatCurrency(totalValue),
            tone: "default",
            testid: "stat-value",
          },
        ].map((s) => (
          <div
            key={s.label}
            data-testid={s.testid}
            className="bg-white rounded-xl border border-gray-200 p-5 shadow-[0_2px_12px_rgba(0,0,0,0.04)]"
          >
            <div className="text-xs font-semibold tracking-wider uppercase text-gray-500">
              {s.label}
            </div>
            <div
              className={`mt-2 font-display font-bold text-3xl tracking-tight ${
                s.tone === "primary" ? "text-[#0055FF]" : "text-gray-900"
              }`}
            >
              {s.value}
            </div>
          </div>
        ))}
      </div>

      {/* Tabs */}
      <div
        data-testid="retailer-tabs"
        className="mt-10 flex items-center gap-1 border-b border-gray-200 overflow-x-auto"
      >
        <TabButton
          active={activeTab === "overview"}
          onClick={() => setActiveTab("overview")}
          testid="tab-overview"
          icon={LayoutDashboard}
          label="Overview"
        />
        <TabButton
          active={activeTab === "queue"}
          onClick={() => setActiveTab("queue")}
          testid="tab-queue"
          icon={Boxes}
          label="Fulfillment Queue"
          badge={queueItems.length}
        />
        <TabButton
          active={activeTab === "email"}
          onClick={() => setActiveTab("email")}
          testid="tab-email"
          icon={Mail}
          label="Email Notifications"
        />
        {isAdmin && (
          <TabButton
            active={activeTab === "admin"}
            onClick={() => setActiveTab("admin")}
            testid="tab-admin"
            icon={Settings}
            label="Admin Panel"
          />
        )}
      </div>

      {/* System Status — visible to all roles, scoped to current tenant */}
      <SystemStatusCard />

      {activeTab === "overview" && (
        <OverviewTab
          receipts={overviewReceipts}
          loading={loading}
          mintingId={mintingId}
          onMint={handleMint}
          onDelete={handleDelete}
          totalMinted={totalMinted}
          totalPending={totalPending}
          onOpenDialog={setMintTarget}
          onViewActivity={setActivityTarget}
          onRequeue={handleRequeue}
          onOpenDetail={setDetailTarget}
          sort={overviewSort}
          onSort={toggleSort(overviewSort, setOverviewSort)}
          filter={overviewFilter}
          filterCounts={filterCounts}
          onFilterChange={setOverviewFilter}
          search={overviewSearch}
          onSearch={setOverviewSearch}
          page={overviewSafePage}
          pageSize={overviewPageSize}
          totalCount={overviewTotal}
          rangeStart={overviewTotal === 0 ? 0 : overviewStart + 1}
          rangeEnd={overviewEnd}
          totalPages={overviewTotalPages}
          onPageChange={setOverviewPage}
          onPageSizeChange={setOverviewPageSize}
        />
      )}

      {activeTab === "queue" && (
        <QueueTab
          items={filteredQueue}
          total={queueItems.length}
          search={queueSearch}
          onSearch={setQueueSearch}
          loading={loading}
          onRefresh={load}
          onFulfill={setFulfillTarget}
          onOpenDialog={setMintTarget}
          onOpenDetail={setDetailTarget}
          sort={queueSort}
          onSort={toggleSort(queueSort, setQueueSort)}
        />
      )}

      {activeTab === "email" && <EmailNotificationsPanel />}

      {activeTab === "admin" && isAdmin && <AdminPanel onResetDemo={handleReset} />}

      <ReviewDialog
        open={!!reviewData}
        data={reviewData}
        onChange={setReviewData}
        onClose={() => setReviewData(null)}
        onSave={handleSave}
      />
      <MintedDialog
        open={!!mintedData}
        receipt={mintedData}
        onClose={() => setMintedData(null)}
      />
      <UploadModal
        open={uploadOpen}
        onClose={() => setUploadOpen(false)}
        onFiles={handleFiles}
        uploading={uploading}
      />
      <QuickIssueDialog
        open={quickIssueOpen}
        onClose={() => setQuickIssueOpen(false)}
        onIssued={() => {
          // refresh the list so the new minted record appears in Overview
          load();
        }}
      />
      <FulfillDialog
        open={!!fulfillTarget}
        receipt={fulfillTarget}
        onClose={() => setFulfillTarget(null)}
        onFulfilled={onFulfilled}
      />
      <MintDetailsDialog
        open={!!mintTarget}
        receipt={mintTarget}
        onClose={() => setMintTarget(null)}
        onUpdated={() => {
          setMintTarget(null);
          load();
        }}
      />
      <ActivityLogDialog
        open={!!activityTarget}
        receipt={activityTarget}
        onClose={() => setActivityTarget(null)}
      />
      <RecordDetailDrawer
        open={!!detailTarget}
        receipt={detailTarget}
        onClose={() => setDetailTarget(null)}
        onFulfill={(r) => {
          setDetailTarget(null);
          setMintTarget(r);
        }}
        onResendEmail={async (r) => {
          try {
            await api.post(`/receipts/${r.id}/resend_email`);
            toast.success(`Email notification sent to ${r.customer_email}`);
            await load();
          } catch (e) {
            toast.error(e?.response?.data?.detail || "Resend failed");
          }
        }}
      />
    </div>
  );
}

function TabButton({ active, onClick, testid, icon: Icon, label, badge }) {
  return (
    <button
      onClick={onClick}
      data-testid={testid}
      className={`relative inline-flex items-center gap-2 px-4 py-3 text-sm font-semibold transition ${
        active
          ? "text-[#0055FF]"
          : "text-gray-500 hover:text-gray-900"
      }`}
    >
      <Icon size={14} />
      {label}
      {badge !== undefined && badge > 0 && (
        <span
          data-testid={`${testid}-badge`}
          className={`ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full text-[11px] font-bold ${
            active
              ? "bg-[#0055FF] text-white"
              : "bg-amber-100 text-amber-800 border border-amber-200"
          }`}
        >
          {badge}
        </span>
      )}
      {active && (
        <span className="absolute left-0 right-0 -bottom-px h-[2px] bg-[#0055FF]" />
      )}
    </button>
  );
}

function OverviewTab({
  receipts,
  loading,
  mintingId,
  onMint,
  onDelete,
  totalMinted,
  totalPending,
  onOpenDialog,
  onViewActivity,
  onRequeue,
  onOpenDetail,
  sort,
  onSort,
  filter,
  filterCounts,
  onFilterChange,
  search,
  onSearch,
  page,
  pageSize,
  totalCount,
  rangeStart,
  rangeEnd,
  totalPages,
  onPageChange,
  onPageSizeChange,
}) {
  return (
    <div className="mt-8">
      <FilterChipBar
        active={filter}
        counts={filterCounts}
        onChange={onFilterChange}
      />
      <div className="flex flex-col gap-3 mb-4">
        <div className="flex items-end justify-between">
          <h2 className="font-display font-semibold text-xl text-gray-900">
            All receipts
          </h2>
          <div className="text-xs text-gray-500 font-mono">
            {totalPending} pending · {totalMinted} certified
          </div>
        </div>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="relative flex-1 min-w-[240px] max-w-md">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
            />
            <input
              type="search"
              placeholder="Search by store, item, SKU, email, tx hash, status…"
              value={search}
              onChange={(e) => onSearch(e.target.value)}
              data-testid="overview-search"
              className="w-full pl-9 pr-3 py-2 rounded-md border border-gray-200 text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
            />
          </div>
          <div className="flex items-center gap-2 text-xs text-gray-600">
            <label htmlFor="overview-page-size" className="font-medium">
              Show
            </label>
            <select
              id="overview-page-size"
              data-testid="overview-page-size"
              value={pageSize}
              onChange={(e) => onPageSizeChange(Number(e.target.value))}
              className="rounded-md border border-gray-200 bg-white px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30"
            >
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="font-medium">per page</span>
          </div>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        <div className="overflow-x-auto rounded-xl">
          <table className="w-full text-sm" data-testid="receipts-table">
            <thead>
              <tr className="bg-gray-50/70 border-b border-gray-200">
                <SortableHeader label="Store" column="store" sort={sort} onSort={onSort} />
                <SortableHeader label="Item" column="item" sort={sort} onSort={onSort} />
                <SortableHeader
                  label="Purchase Date"
                  column="purchase_date"
                  sort={sort}
                  onSort={onSort}
                  tooltip="Date of original transaction"
                />
                <SortableHeader
                  label="Verified Date"
                  column="minted_at"
                  sort={sort}
                  onSort={onSort}
                  tooltip="Date certificate was issued"
                />
                <SortableHeader label="Price" column="total" sort={sort} onSort={onSort} align="right" />
                <SortableHeader label="Status" column="status" sort={sort} onSort={onSort} />
                <th className="px-5 py-3.5 text-right text-[11px] font-semibold tracking-wider uppercase text-gray-500">
                  Action
                </th>
              </tr>
            </thead>
            <tbody className="stagger">
              {loading && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-gray-500">
                    Loading…
                  </td>
                </tr>
              )}
              {!loading && receipts.length === 0 && (
                <tr>
                  <td colSpan={7} className="px-5 py-16 text-center text-gray-500">
                    No receipts yet. Use “Upload Receipt” to add one.
                  </td>
                </tr>
              )}
              {receipts.map((r) => {
                const item = r.items?.[0];
                return (
                  <tr
                    key={r.id}
                    data-testid={`row-${r.id}`}
                    onClick={() => onOpenDetail?.(r)}
                    className="border-b border-gray-100 last:border-0 hover:bg-gray-50/60 transition-colors cursor-pointer"
                  >
                    <td className="px-5 py-4">
                      <div className="font-semibold text-gray-900">
                        {r.store_name}
                      </div>
                      <div className="font-mono text-xs text-gray-400 mt-0.5">
                        {r.receipt_id}
                      </div>
                      {r.source === "woocommerce" && (
                        <span
                          data-testid={`source-woo-${r.id}`}
                          className="inline-flex items-center gap-1 mt-1.5 bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide"
                        >
                          via WooCommerce
                        </span>
                      )}
                    </td>
                    <td className="px-5 py-4">
                      <div className="text-gray-900">{item?.name || "—"}</div>
                      {item?.sku && (
                        <div className="font-mono text-xs text-gray-400 mt-0.5">
                          SKU: {item.sku}
                        </div>
                      )}
                    </td>
                    <td
                      className="px-5 py-4 text-gray-600"
                      data-testid={`purchase-date-${r.id}`}
                    >
                      {formatDate(r.purchase_date)}
                    </td>
                    <td
                      className="px-5 py-4 text-gray-600"
                      data-testid={`verified-date-${r.id}`}
                    >
                      {r.minted_at ? (
                        formatDate(r.minted_at)
                      ) : (
                        <span className="text-gray-300">—</span>
                      )}
                    </td>
                    <td className="px-5 py-4 text-right font-mono text-gray-900">
                      {formatCurrency(r.total, r.currency)}
                    </td>
                    <td className="px-5 py-4">
                      <StatusBadge status={r.status} />
                      {r.status === "minted" && r.tx_hash && (
                        <div className="font-mono text-[10px] text-gray-400 mt-1">
                          tx {truncHash(r.tx_hash, 6, 4)}
                        </div>
                      )}
                    </td>
                    <td
                      className="px-5 py-4 text-right"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="flex items-center justify-end gap-2">
                        <RowAction
                          r={r}
                          mintingId={mintingId}
                          onMint={onMint}
                          onOpenDialog={onOpenDialog}
                        />
                        <RowMenu
                          r={r}
                          onViewActivity={onViewActivity}
                          onRequeue={onRequeue}
                          onDelete={onDelete}
                        />
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <div
          data-testid="overview-pagination"
          className="flex items-center justify-between gap-3 px-5 py-3 border-t border-gray-100 text-xs text-gray-600"
        >
          <div data-testid="overview-pagination-summary">
            {totalCount === 0 ? (
              <span className="text-gray-400">No matching records</span>
            ) : (
              <>
                Showing{" "}
                <span className="font-semibold text-gray-900">
                  {rangeStart}–{rangeEnd}
                </span>{" "}
                of <span className="font-semibold text-gray-900">{totalCount}</span>
              </>
            )}
          </div>
          <div className="flex items-center gap-1">
            <button
              type="button"
              onClick={() => onPageChange(Math.max(1, page - 1))}
              disabled={page <= 1}
              data-testid="overview-page-prev"
              className="px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="px-3 text-gray-500" data-testid="overview-page-indicator">
              Page <span className="font-semibold text-gray-900">{page}</span> /{" "}
              {totalPages}
            </span>
            <button
              type="button"
              onClick={() => onPageChange(Math.min(totalPages, page + 1))}
              disabled={page >= totalPages}
              data-testid="overview-page-next"
              className="px-2.5 py-1.5 rounded-md border border-gray-200 bg-white text-gray-700 hover:border-[#0055FF] hover:text-[#0055FF] disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function QueueTab({ items, total, search, onSearch, loading, onRefresh, onFulfill, onOpenDialog, onOpenDetail, sort, onSort }) {
  const openAction = (r) => {
    // Use the new unified modal for any non-trivial status; fall back to the
    // legacy single-field dialog for quick-fulfill cases (pending_fulfillment
    // without any details to add).
    if (r.status === "pending_fulfillment") {
      onFulfill(r);
    } else {
      onOpenDialog(r);
    }
  };
  return (
    <div data-testid="fulfillment-queue" className="mt-8">
      <div className="flex items-end justify-between mb-4 gap-4">
        <div>
          <h2 className="font-display font-semibold text-xl text-gray-900">
            Fulfillment Queue
          </h2>
          <div className="text-xs text-gray-500 mt-1">
            Records awaiting a serial number. Submitting one issues the certificate
            on-chain and emails the customer.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <input
            type="text"
            placeholder="Search order, receipt, item, email…"
            value={search}
            onChange={(e) => onSearch(e.target.value)}
            data-testid="queue-search"
            className="w-72 max-w-[50vw] rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0055FF]/30 focus:border-[#0055FF]"
          />
          <button
            onClick={onRefresh}
            data-testid="queue-refresh"
            title="Reload queue"
            className="inline-flex items-center gap-1.5 bg-white text-gray-700 border border-gray-200 text-sm font-medium px-3 py-2 rounded-md hover:border-[#0055FF] hover:text-[#0055FF] transition"
          >
            <RefreshCcw size={14} />
            Refresh
          </button>
        </div>
      </div>

      <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-[0_2px_12px_rgba(0,0,0,0.04)]">
        {loading ? (
          <div className="px-5 py-16 text-center text-gray-500">Loading…</div>
        ) : total === 0 ? (
          <div
            data-testid="queue-empty"
            className="px-5 py-20 text-center"
          >
            <div className="mx-auto h-12 w-12 rounded-xl bg-green-50 text-green-600 flex items-center justify-center">
              <BadgeCheck size={22} />
            </div>
            <div className="mt-4 font-semibold text-gray-900">
              No pending fulfillment items
            </div>
            <div className="mt-1 text-sm text-gray-500">
              New WooCommerce orders awaiting serials will appear here.
            </div>
          </div>
        ) : items.length === 0 ? (
          <div className="px-5 py-14 text-center text-gray-500 text-sm">
            No items match “{search}”.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="queue-table">
              <thead>
                <tr className="bg-gray-50/70 border-b border-gray-200">
                  <SortableHeader label="Item" column="item" sort={sort} onSort={onSort} />
                  <SortableHeader label="Order" column="order_id" sort={sort} onSort={onSort} />
                  <th className="px-5 py-3.5 text-left text-[11px] font-semibold tracking-wider uppercase text-gray-500">
                    Source
                  </th>
                  <SortableHeader label="Customer" column="customer" sort={sort} onSort={onSort} />
                  <SortableHeader label="Purchase Date" column="purchase_date" sort={sort} onSort={onSort} tooltip="Date of original transaction" />
                  <SortableHeader label="Status" column="status" sort={sort} onSort={onSort} />
                  <th className="px-5 py-3.5 text-right text-[11px] font-semibold tracking-wider uppercase text-gray-500">
                    Action
                  </th>
                </tr>
              </thead>
              <tbody>
                {items.map((r) => {
                  const item = r.items?.[0];
                  return (
                    <tr
                      key={r.id}
                      data-testid={`queue-row-${r.id}`}
                      onClick={() => onOpenDetail?.(r)}
                      className="border-b border-gray-100 last:border-0 hover:bg-amber-50/30 transition-colors cursor-pointer"
                    >
                      <td className="px-5 py-4">
                        <div className="font-semibold text-gray-900">
                          {item?.name || "Item"}
                        </div>
                        <div className="font-mono text-xs text-gray-400 mt-0.5">
                          {r.receipt_id}
                        </div>
                      </td>
                      <td className="px-5 py-4">
                        <div className="font-mono text-sm text-gray-900">
                          {r.source_order_id || "—"}
                        </div>
                        {r.source_unit_index && (
                          <div className="text-xs text-gray-400 mt-0.5">
                            Unit {r.source_unit_index}
                          </div>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {r.source === "woocommerce" ? (
                          <span className="inline-flex items-center gap-1 bg-purple-50 text-purple-700 border border-purple-200 px-1.5 py-0.5 rounded text-[10px] font-semibold tracking-wide">
                            WooCommerce
                          </span>
                        ) : (
                          <span className="text-xs">{r.store_name}</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {r.customer_email || (
                          <span className="text-gray-400 text-xs">—</span>
                        )}
                      </td>
                      <td className="px-5 py-4 text-gray-600">
                        {formatDate(r.purchase_date)}
                      </td>
                      <td className="px-5 py-4">
                        <StatusBadge status={r.status} />
                      </td>
                      <td
                        className="px-5 py-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <button
                          onClick={() => openAction(r)}
                          data-testid={`fulfill-btn-${r.id}`}
                          className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-xs font-semibold px-3 py-1.5 rounded-md shadow-sm hover:bg-[#0044CC] transition"
                        >
                          <Barcode size={13} />
                          {r.status === "mint_failed"
                            ? "Retry"
                            : r.status === "ready_to_mint"
                              ? "Issue"
                              : "Fulfill"}
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
